"use client";

import { ReactNode, useState } from "react";

type WhyCardItem = {
    id: string;
    title: string;
    description: string;
    details: string;
    bgClass: string;
    iconColorClass: string;
    icon: (className: string) => ReactNode;
};

function CrosshairIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function BoltIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <path d="M13.8 3 5.5 13.2h5l-1.2 7.8 9-11h-5.1L13.8 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
    );
}

function ChartIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <path d="M4 4v14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M8 15V9M12 15V6M16 15v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function GlobeIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M4 12h16M12 4c2.1 2.2 3.2 5.1 3.2 8S14.1 17.8 12 20M12 4c-2.1 2.2-3.2 5.1-3.2 8S9.9 17.8 12 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function TruckIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <path d="M3 7.5h10.5v8H3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M13.5 10h3l2.5 2.8V15h-5.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <circle cx="7" cy="16.5" r="1.7" stroke="currentColor" strokeWidth="2" />
            <circle cx="16.5" cy="16.5" r="1.7" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}

function CreditCardIcon(className: string) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}

const cardItems: WhyCardItem[] = [
    {
        id: "reduce-emissions",
        title: "Reduce Emissions",
        description: "Cut fleet carbon footprint by up to 80% through strategic electrification and optimized routing.",
        details:
            "Model route-by-route electrification to reduce tailpipe emissions while preserving operational reliability and SLA commitments.",
        bgClass: "bg-gradient-to-br from-rose-50 to-rose-100/60",
        iconColorClass: "text-red-400",
        icon: CrosshairIcon,
    },
    {
        id: "lower-fuel-costs",
        title: "Lower Fuel Costs",
        description: "EVs cost 60–70% less per mile. Ormae finds the optimal transition points for maximum savings.",
        details:
            "Compare fuel, charging, maintenance, and utilization tradeoffs to prioritize transitions where savings and payback are strongest.",
        bgClass: "bg-gradient-to-br from-amber-50 to-orange-50/70",
        iconColorClass: "text-amber-500",
        icon: BoltIcon,
    },
    {
        id: "meet-regulations",
        title: "Meet Regulations",
        description: "Stay ahead of tightening emissions regulations with a proactive, phased compliance strategy.",
        details:
            "Generate auditable timelines aligned with regional policy milestones, helping you avoid penalties and de-risk capex planning.",
        bgClass: "bg-gradient-to-br from-slate-100 to-slate-200/70",
        iconColorClass: "text-sky-500",
        icon: ChartIcon,
    },
    {
        id: "sustainability-goals",
        title: "Sustainability Goals",
        description: "Align fleet operations with ESG targets and demonstrate tangible progress to stakeholders.",
        details:
            "Track KPI improvements over time and turn decarbonization progress into investor, board, and customer-ready reporting outputs.",
        bgClass: "bg-gradient-to-br from-emerald-50 to-slate-100/70",
        iconColorClass: "text-emerald-500",
        icon: GlobeIcon,
    },
    {
        id: "fleet-optimization",
        title: "Fleet Optimization",
        description: "AI-driven analysis identifies which vehicles to transition first for maximum impact.",
        details:
            "Rank vehicles by mileage, duty cycle, depot readiness, and replacement timing to sequence transitions with minimal disruption.",
        bgClass: "bg-gradient-to-br from-zinc-100 to-zinc-200/70",
        iconColorClass: "text-emerald-950",
        icon: TruckIcon,
    },
    {
        id: "carbon-credits",
        title: "Carbon Credits",
        description: "Strategic carbon credit purchasing fills the gap while your fleet transitions.",
        details:
            "Offset residual emissions during interim phases while EV share scales, preserving momentum toward net-zero commitments.",
        bgClass: "bg-gradient-to-br from-lime-50 to-stone-100/70",
        iconColorClass: "text-lime-600",
        icon: CreditCardIcon,
    },
];

export function WhyCardsSection() {
    const [activeCardId, setActiveCardId] = useState<string | null>(null);
    const activeCard = cardItems.find((card) => card.id === activeCardId) ?? null;

    return (
        <>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {cardItems.map((card) => (
                    <article
                        key={card.id}
                        className={`flex min-h-[312px] flex-col rounded-[28px] border border-black/5 p-10 ${card.bgClass}`}
                    >
                        <div>
                            {card.icon(`${card.iconColorClass} h-8 w-8`)}
                            <h3 className="mt-8 text-[18px] leading-[1.25] font-semibold text-zinc-900">{card.title}</h3>
                            <p className="mt-5 max-w-[34ch] text-[14px] leading-7 text-zinc-600">{card.description}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setActiveCardId(card.id)}
                            className="mt-7 inline-flex w-fit items-center text-[12px] font-semibold text-emerald-400 transition hover:text-emerald-500"
                        >
                            Click for details →
                        </button>
                    </article>
                ))}
            </div>

            {activeCard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setActiveCardId(null)}>
                    {activeCard.id === "reduce-emissions" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-rose-50 p-3">
                                        {CrosshairIcon("h-7 w-7 text-red-400")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Reduce Emissions</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">Cut your fleet&apos;s carbon footprint dramatically</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-rose-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-red-400">80%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Max Emission Cut</p>
                                </div>
                                <div className="rounded-2xl bg-emerald-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">12.4t</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">CO₂ Saved/EV/yr</p>
                                </div>
                                <div className="rounded-2xl bg-amber-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-amber-500">27%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Transport Share</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">The Carbon Imperative</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                Fleet vehicles are among the largest contributors to organizational carbon footprints. A single heavy-duty diesel
                                truck emits approximately 16g of CO₂ per ton-mile — over hundreds of vehicles, this adds up to thousands of tons
                                annually.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                Ormae&apos;s optimization engine identifies the highest-emitting vehicles and prioritizes them for electrification,
                                delivering maximum carbon reduction with each phase.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">How We Measure Impact</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-400" />Well-to-wheel emissions analysis across your entire fleet</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Scope 1, 2, and 3 emission tracking aligned with GHG Protocol</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Real-time dashboards showing cumulative CO₂ reduction</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />Verified carbon credit integration for residual emissions</li>
                            </ul>
                        </div>
                    ) : activeCard.id === "lower-fuel-costs" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-amber-50 p-3">
                                        {BoltIcon("h-7 w-7 text-amber-500")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Lower Fuel Costs</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">Electrification pays for itself — here&apos;s the math</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-amber-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-amber-500">60–70%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Per-Mile Savings</p>
                                </div>
                                <div className="rounded-2xl bg-emerald-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">$4,200</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Savings/EV/yr</p>
                                </div>
                                <div className="rounded-2xl bg-sky-50 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-sky-500">3.2yr</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Typical Payback</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">The Economics of Electrification</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                Electric vehicles cost dramatically less per mile. With diesel at $3.80/gallon and electricity at $0.13/kWh,
                                fleets switching to EVs see immediate savings — often $4,000–$6,000 per vehicle annually.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                Maintenance costs drop an additional 30–40% thanks to fewer moving parts and regenerative braking.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">Financial Benefits</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Fuel cost: EVs ~$0.04/mile vs. $0.15/mile for diesel</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Federal tax credits up to $40K per commercial EV (IRA)</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />State incentives can cover 30–50% of purchase price</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-lime-400" />Ormae optimizes transition timing for max financial returns</li>
                            </ul>
                        </div>
                    ) : activeCard.id === "meet-regulations" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-sky-50 p-3">
                                        {ChartIcon("h-7 w-7 text-sky-500")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Meet Regulations</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">Stay ahead of tightening emission mandates</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-sky-500">2030</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">EPA Deadline</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">35+</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">States w/ Mandates</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-red-400">$50K+</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Non-Compliance Fine</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">Regulatory Landscape</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                EPA&apos;s 2024 heavy-duty standards require 60% NOx reduction by 2031. California&apos;s ACF rule mandates 100%
                                zero-emission medium/heavy-duty vehicles by 2036.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                European (Euro 7) and Canadian regulations mirror these aggressive timelines. Non-compliance carries significant
                                penalties.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">How Ormae Keeps You Compliant</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />Regulatory deadline mapping in your transition timeline</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-400" />Proactive compliance tracking — never miss a deadline</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Jurisdiction-specific rules for multi-state operations</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Automated report generation for EPA, CARB, and states</li>
                            </ul>
                        </div>
                    ) : activeCard.id === "sustainability-goals" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-emerald-50 p-3">
                                        {GlobeIcon("h-7 w-7 text-emerald-500")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Sustainability Goals</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">Demonstrate tangible ESG progress to stakeholders</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">89%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Investors Weigh ESG</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-lime-500">73%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Consumers Go Green</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-amber-500">2.3x</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">ESG Valuation Lift</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">ESG as a Competitive Advantage</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                Institutional investors tie capital allocation to ESG performance. Companies with strong sustainability metrics see
                                higher valuations, lower cost of capital, and better access to green financing.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                Fleet decarbonization is one of the most measurable ESG initiatives — with clear, quantifiable metrics.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">Stakeholder Impact</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />CDP and TCFD-aligned reporting from your dashboard</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-lime-400" />Science-Based Targets initiative (SBTi) alignment</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Customer-facing sustainability badges & impact reports</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />Green bond eligibility through demonstrated reductions</li>
                            </ul>
                        </div>
                    ) : activeCard.id === "fleet-optimization" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-zinc-100 p-3">
                                        {TruckIcon("h-7 w-7 text-emerald-950")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Fleet Optimization</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">AI identifies the highest-impact vehicles to transition first</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-950">AI</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Optimization Engine</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">1000+</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Scenarios Modeled</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-lime-500">94%</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Execution Rate</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">Intelligent Transition Planning</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                Not all vehicles should transition simultaneously. Ormae&apos;s AI analyzes route patterns, daily mileage, depot proximity,
                                payload requirements, and TCO to determine the optimal sequence.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                High-mileage urban routes yield the greatest early ROI, while long-haul may wait for next-gen batteries.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">Optimization Factors</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-950" />Vehicle-level: age, mileage, duty cycle, replacement cost</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Charging infra: depot locations, grid capacity, solar</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />10-year TCO modeling across vehicle lifecycle</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />ICE resale value forecasting for optimal timing</li>
                            </ul>
                        </div>
                    ) : activeCard.id === "carbon-credits" ? (
                        <div
                            className="w-full max-w-[820px] rounded-3xl border border-black/10 bg-white p-7 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="rounded-2xl bg-lime-50 p-3">
                                        {CreditCardIcon("h-7 w-7 text-lime-600")}
                                    </div>
                                    <div>
                                        <h3 className="text-[36px] leading-none font-semibold text-[#1f1d35]">Carbon Credits</h3>
                                        <p className="mt-2 text-[14px] text-zinc-500">Bridge the gap while your fleet transitions</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-2xl text-zinc-500 hover:bg-zinc-200"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-lime-500">$45-80</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Price/Ton CO₂</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-emerald-500">Verified</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Gold Standard & VCS</p>
                                </div>
                                <div className="rounded-2xl bg-stone-100 p-4 text-center">
                                    <p className="text-[38px] leading-none font-semibold text-amber-500">Biennial</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">Purchase Cycle</p>
                                </div>
                            </div>

                            <h4 className="mt-6 text-[16px] font-semibold text-emerald-950">Strategic Carbon Offset Management</h4>
                            <p className="mt-2 text-[14px] leading-8 text-zinc-600">
                                During the multi-year transition, carbon credits bridge the gap between current emissions and net-zero. Ormae recommends
                                biennial purchases aligned with milestones to optimize cost while maintaining neutrality.
                            </p>
                            <p className="mt-3 text-[14px] leading-8 text-zinc-600">
                                All credits sourced from verified registries with full chain-of-custody documentation.
                            </p>

                            <h4 className="mt-4 text-[16px] font-semibold text-emerald-950">Credit Strategy</h4>
                            <ul className="mt-3 space-y-2 text-[14px] text-zinc-600">
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-lime-400" />Reforestation projects with co-benefits</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Direct air capture credits for permanent removal</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />Forward purchasing to lock in current pricing</li>
                                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Decreasing needs as EV fleet grows — auto-recalculation</li>
                            </ul>
                        </div>
                    ) : (
                        <div
                            className="w-full max-w-xl rounded-3xl border border-black/10 bg-white p-8 text-zinc-900 shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <h3 className="text-3xl font-semibold text-emerald-950">{activeCard.title}</h3>
                                <button
                                    type="button"
                                    onClick={() => setActiveCardId(null)}
                                    className="rounded-full border border-zinc-300 px-3 py-1 text-sm font-semibold text-zinc-600 hover:bg-zinc-100"
                                >
                                    Close
                                </button>
                            </div>
                            <p className="mt-5 text-base leading-7 text-zinc-600">{activeCard.description}</p>
                            <p className="mt-4 text-base leading-7 text-zinc-600">{activeCard.details}</p>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
